import loadable from '@loadable/component';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button, Col, Row, Table } from 'reactstrap';

import Analytics from '../../../analytics/Analytics';
import globalTranslations from '../../../i18n/globalTranslations';
import FullProduct from '../../../models/product/FullProduct';
import FullProductMatrix from '../../../models/product/FullProductMatrix';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import ConfigStore from '../../../store/ConfigStore';
import CurrencyStore from '../../../store/CurrencyStore';
import UIStore from '../../../store/UIStore';
import ProductAvailabilityType from '../../../types/ProductAvailabilityType';
import RequestState from '../../../types/RequestState';
import { productCountValid, quantityValid } from '../../../util/formValidators';
import { calculateTotal, roundWithPrecision } from '../../../util/number';
import CustomCol from '../../bootstrap/CustomCol';
import Icon from '../../common/Icon';
import { initialImageOrigin } from '../../common/ImageLightbox/ImageLightboxProductImage';
import { DEFAULT_MAX_QUANTITY } from '../ProductAddToCart';
import ProductCollectionMatrixHeaderRow from '../ProductCollectionMatrixHeaderRow';
import ProductCollectionMatrixTableBody from '../ProductCollectionMatrixTableBody';
import ProductSizeGuides from '../ProductSizeGuides';

const ImageLightbox = loadable(() =>
  import(
    /* webpackChunkName: "common" */ '../../common/ImageLightbox/ImageLightboxModal'
  )
);

@observer
export class ProductCollectionMatrix extends Component {
  constructor(props) {
    super(props);

    const { product } = props;

    product.sortProductImages('for_color_id');

    this.state = {
      selectedProducts: [],
      analyticsProducts: [],
      mainImageIndex: null,
      lightboxIsOpen: false,
      formError: false,
    };

    product.images.forEach((_, index) => {
      this.lightboxInitialState = {
        ...this.lightboxInitialState,
        [index]: {
          focus: false,
          scale: 1,
          origin: initialImageOrigin,
        },
      };
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.product.id !== this.props.product.id) {
      this.setState({
        selectedProducts: null,
        analyticsProducts: null,
      });
    }
  }

  getImageLightbox = () => {
    const { product } = this.props;
    const { mainImageIndex, lightboxIsOpen } = this.state;

    if (
      product.images.length === 0 ||
      !lightboxIsOpen ||
      mainImageIndex === null
    ) {
      return null;
    }

    this.lightboxInitialState.selectedImage = mainImageIndex;

    return (
      <ImageLightbox
        initialState={this.lightboxInitialState}
        mainImageIndex={mainImageIndex}
        lightboxIsOpen={lightboxIsOpen}
        product={product}
        onClick={this.toggleLightbox.bind(this, false)}
        images={product.images}
      />
    );
  };

  toggleLightbox = (lightboxIsOpen) => this.setState({ lightboxIsOpen });

  getCollectionRowElements = () => this.props.productMatrix.getRowElements();

  addSelectedProducts = () => {
    const { cartStore, analytics, accountStore, currencyStore } = this.props;
    const { selectedProducts, analyticsProducts } = this.state;
    selectedProducts &&
      analyticsProducts &&
      selectedProducts.length > 0 &&
      analyticsProducts.length > 0 &&
      cartStore
        .populate(selectedProducts, accountStore.showCartMatrix)
        .then(() => {
          const totalValue = this.calculateTotalAnalyticsValue(
            currencyStore,
            analyticsProducts
          );
          analytics.addToCart(analyticsProducts, totalValue);
          analytics.cartStatus({
            cart: cartStore.cart,
          });
        });
  };

  calculateTotalAnalyticsValue() {
    const { analyticsProducts } = this.state;
    const precision = 4;
    return roundWithPrecision(
      calculateTotal(
        analyticsProducts.map((product) => product.price * product.quantity),
        precision
      ),
      precision
    );
  }

  renderProductMatrixTitles = () => {
    const titles = this.props.productMatrix.getNames();
    return (
      <Col>
        <div className="ProductCollectionMatrix__column-row-label">
          <span>
            {titles.columnName}
            {titles.columnName && titles.rowName && ' / '}
            {titles.rowName}
          </span>
        </div>
      </Col>
    );
  };

  setMainImageIndex = (mainImageIndex) => {
    this.setState({ mainImageIndex }, () => this.toggleLightbox(true));
  };

  handleQuantityChange = (event, productCollectionElementItem) => {
    const name = event.target.name;
    const selectedProducts = this.state.selectedProducts;
    const id = productCollectionElementItem.product.id;
    const maxQuantity = this.getMaxQuantity(id);
    const qty = this.getQuantity(Number(event.target.value), maxQuantity);

    this.setState({ [name]: qty }, () => {
      const quantity = this.state[name];

      this.validateProductQuantity(
        productCollectionElementItem.row_id,
        productCollectionElementItem.column_id,
        maxQuantity,
        name
      );

      this.findSelectedProduct(selectedProducts, id, quantity);
      this.modifySelectedProductsForAnalytics();
    });
  };

  handleQuantityFocus = (productCollectionElementItem) => {
    const inputTooltip = `${productCollectionElementItem.row_id}--${productCollectionElementItem.column_id}--tooltip`;
    this.setState({ [inputTooltip]: true });
  };

  handleQuantityBlur = (productCollectionElementItem) => {
    const inputTooltip = `${productCollectionElementItem.row_id}--${productCollectionElementItem.column_id}--tooltip`;
    this.setState({ [inputTooltip]: false });
  };

  getMaxQuantity = (activeProductId) => {
    const { accountStore, configStore, product } = this.props;

    const maxAccountQuantity =
      accountStore.account && accountStore.account.max_product_quantity > 0
        ? accountStore.account.max_product_quantity
        : DEFAULT_MAX_QUANTITY;

    const maxBackorderQuantity =
      product.availability_type === ProductAvailabilityType.ALLOW_BACKORDER
        ? configStore.product.backorderLimit > 0
          ? configStore.product.backorderLimit
          : DEFAULT_MAX_QUANTITY
        : DEFAULT_MAX_QUANTITY;

    const maxNormalProductQuantity =
      product.availability_type !== ProductAvailabilityType.ALLOW_BACKORDER
        ? product.canBeOrderedOutOfStock
          ? DEFAULT_MAX_QUANTITY
          : this.getMaxByPackage(product, activeProductId)
        : DEFAULT_MAX_QUANTITY;

    return Math.min(
      maxAccountQuantity,
      maxBackorderQuantity,
      maxNormalProductQuantity,
      DEFAULT_MAX_QUANTITY
    );
  };

  getQuantity = (qty, maxQuantity) => {
    const { product } = this.props;

    const freeQuantity = product.getFreeQuantity(product.id);

    const cannotBeOrdered =
      freeQuantity <= 0 && !product.canBeOrderedOutOfStock && qty < 0;
    const canBeOrdered = freeQuantity > 0 && qty > 0 && qty <= maxQuantity;
    const exceedsMaxQuantity = qty >= maxQuantity;
    const nonNegativeQuantity = qty > 0 && qty;

    return cannotBeOrdered
      ? 0
      : canBeOrdered
      ? nonNegativeQuantity
      : exceedsMaxQuantity
      ? maxQuantity
      : nonNegativeQuantity;
  };

  getMaxByPackage = (product, activeProductId) => {
    return product.sellInPackage
      ? Math.floor(
          product.getFreeQuantity(activeProductId) / product.package_size
        ) * product.package_size
      : product.getFreeQuantity(activeProductId);
  };

  validateProductQuantity(
    productCollectionRowElementId,
    productCollectionColumnElementId,
    maxQuantity,
    name
  ) {
    const quantityStep = this.getQuantityStep();
    const currentQuantity =
      this.state[
        `qty--${productCollectionRowElementId}--${productCollectionColumnElementId}`
      ];

    if (quantityStep > 1) {
      const validQuantity = quantityValid(maxQuantity, quantityStep);
      const validProductCount = productCountValid(quantityStep);
      const invalidQuantity =
        validQuantity(currentQuantity) || validProductCount(currentQuantity);

      if (invalidQuantity) {
        const error = { error: invalidQuantity };
        this.setState({
          [name + '--error']: error,
          formError: true,
        });
      }

      if (!invalidQuantity || !currentQuantity) {
        this.setState({
          [name + '--error']: null,
          formError: false,
        });
      }
    }
  }

  getQuantityStep = () => {
    const { product } = this.props;
    return product && product.sellInPackage ? product.package_size : 1;
  };

  findSelectedProduct = (selectedProducts, id, qty) => {
    const selectedProduct = selectedProducts.find(
      (product) => product.id === id
    );
    if (selectedProduct) {
      this.updateOrRemoveSelectedProductQuantity(
        selectedProducts,
        selectedProduct,
        qty
      );
    } else {
      qty > 0 && this.addSelectedProduct(selectedProducts, id, qty);
    }
  };

  updateOrRemoveSelectedProductQuantity = (
    selectedProducts,
    selectedProduct,
    qty
  ) => {
    const productIndex = selectedProducts.findIndex(
      (product) => product.id === selectedProduct.id
    );
    qty > 0 &&
      this.updateSelectedProductQuantity(
        selectedProducts,
        selectedProduct,
        qty,
        productIndex
      );
    (!qty || qty === 0) &&
      this.removeSelectedProduct(selectedProducts, productIndex);
  };

  updateSelectedProductQuantity = (
    selectedProducts,
    selectedProduct,
    qty,
    productIndex
  ) => {
    selectedProduct.qty = qty;
    selectedProducts.splice(productIndex, 1, selectedProduct);
    this.setState({ selectedProducts });
  };

  removeSelectedProduct = (selectedProducts, productIndex) => {
    selectedProducts.splice(productIndex, 1);
    this.setState({ selectedProducts });
  };

  addSelectedProduct = (selectedProducts, id, qty) => {
    selectedProducts.push({
      id,
      qty,
    });
    this.setState({ selectedProducts });
  };

  modifySelectedProductsForAnalytics = () => {
    const { accountStore, product } = this.props;
    const withTax = accountStore.showPricesWithTax;
    const analyticsProducts = this.state.selectedProducts.map(
      (selectedProduct) => {
        const analyticsProduct = {};
        analyticsProduct.product = product;
        analyticsProduct.quantity = selectedProduct.qty;
        analyticsProduct.activeProductId = selectedProduct.id;
        analyticsProduct.price = product.getPrice(withTax, selectedProduct.id);
        return analyticsProduct;
      }
    );

    this.setState({
      analyticsProducts,
    });
  };

  quantityError = (
    productCollectionElementItem,
    productCollectionColumnElementId
  ) => {
    const quantity =
      this.state[
        `qty--${productCollectionElementItem.row_id}--${productCollectionColumnElementId}--error`
      ];

    const formError = quantity && quantity.error;

    if (!formError) {
      return null;
    }

    return (
      <FormFeedback className="ProductCollectionMatrix__invalid-feedback">
        {formError}
      </FormFeedback>
    );
  };

  renderAddSelectedProductsButton = (className) => {
    const { cartStore } = this.props;
    const loading = cartStore.state === RequestState.LOADING;
    return (
      <Button
        className={className}
        onClick={this.addSelectedProducts}
        color="primary"
        disabled={this.state.formError || loading}
        block
      >
        {loading ? (
          <Icon name="circle-o-notch" spin={loading} />
        ) : (
          <FormattedMessage
            id="productCollectionMatrix.addSelectedProducts"
            defaultMessage="Add Selected Products"
          />
        )}
      </Button>
    );
  };

  render() {
    const {
      accountStore,
      configStore,
      uiStore,
      product,
      productMatrix,
      ifProductRowImages,
    } = this.props;
    const productRows = this.getCollectionRowElements();
    const styles = {
      tableLayout:
        !uiStore.isMobile && productRows.length >= 4 ? 'fixed' : 'unset',
    };
    return (
      <div className="ProductCollectionMatrix">
        <Row className="ProductCollectionMatrix__info-row">
          <CustomCol>
            <p className="ProductCollectionMatrix__product-matrix-info">
              <FormattedMessage {...globalTranslations.productMatrixInfo} />
            </p>
          </CustomCol>
        </Row>
        <Row className="ProductCollectionMatrix__buttons-row">
          {!accountStore.isViewOnly && (
            <>
              <CustomCol md={3} xxl={2}>
                {this.renderAddSelectedProductsButton(
                  'ProductCollectionMatrix__info-row--add-selected-products-button'
                )}
              </CustomCol>
              <CustomCol md={9} xxl={2}>
                {product.sizeGuides.length > 0 && (
                  <ProductSizeGuides sizeGuides={product.sizeGuides} />
                )}
              </CustomCol>
            </>
          )}
        </Row>
        <Row>{this.renderProductMatrixTitles()}</Row>
        <Row>
          <Col xs={12}>
            <Table
              className="ProductCollectionMatrix__product-table"
              style={styles}
              striped={!configStore.productPage.matrixDetailedStockInfoEnabled}
            >
              {!uiStore.isMobile && (
                <ProductCollectionMatrixHeaderRow
                  productMatrix={productMatrix}
                />
              )}
              <ProductCollectionMatrixTableBody
                product={product}
                productMatrix={productMatrix}
                ifProductRowImages={ifProductRowImages}
                tooltipStates={this.state}
                onImageClick={this.setMainImageIndex}
                onQuantityChange={this.handleQuantityChange}
              />
            </Table>
          </Col>
        </Row>
        {!accountStore.isViewOnly && (
          <Row className="ProductCollectionMatrix__info-row-footer">
            <CustomCol
              md={{ size: 3, offset: 9 }}
              xxl={{ size: 2, offset: 10 }}
            >
              {this.renderAddSelectedProductsButton(
                'ProductCollectionMatrix__info-row-footer--add-selected-products-button'
              )}
            </CustomCol>
          </Row>
        )}
        {this.getImageLightbox()}
      </div>
    );
  }
}

ProductCollectionMatrix.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  product: modelOf(FullProduct).isRequired,
  productMatrix: modelOf(FullProductMatrix).isRequired,
};

export default injectIntl(
  inject(
    'analytics',
    'accountStore',
    'cartStore',
    'configStore',
    'currencyStore',
    'uiStore'
  )(ProductCollectionMatrix)
);
